import {
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    MenuItem,
    Select,
    Tooltip,
    Typography,
} from '@mui/material';
import { Copy, Download } from 'lucide-react';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useListTargets } from '~/backend/connector-api/connector-components';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import Modal from '~/components/Modal';
import type { RigidRegistration } from '~/domain/patient/rigidRegistration';
import {
    SelectedStructureSetsWithStructures,
    useDicomExport,
} from '~/hooks/export/useDicomExport';
import useExportEditing from '~/hooks/export/useExportEditing';
import { useCopyToClipboard } from '~/hooks/useCopyToClipboard';
import { useImagesCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import AppConfig from '~/utils/appconfig';
import useExportSelections from '~/hooks/export/useExportSelection';
import { useExportStructureSetReadModels } from '~/hooks/export/useExportStructureSetReadModels';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import {
    enableExportMonacoDirFileState,
    exportDatasetsModalOpenState,
    showSensitiveInfoState,
} from '~/state/atoms/viewerAtoms';
import { calculateAge, formatDateCompact } from '~/utils/date';
import { enqueueSnackbar } from '~/utils/notifications';
import ExportConfirmation from './ExportConfirmation';
import ExportHeader from './ExportHeader';
import ExportableItemsList from './ExportItemSelection';

const SEX_MESSAGE_IDS: Record<string, string> = {
    M: 'sex.male',
    F: 'sex.female',
    O: 'sex.other',
};

type PendingConfirmAction = 'export' | 'download' | null;

const ExportDatasetModal = () => {
    const [open, setOpen] = useRecoilState(exportDatasetsModalOpenState);
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    // Tracks which user action (Export-to-TPS or Download-to-disk) is
    // waiting on the ExportConfirmation dialog's Confirm button. We need
    // this because the same confirmation dialog is now the gate for
    // both flows when a dose is part of the selection (SRV-WOR-H-4-1-2:
    // the dose scaling / normalization disclaimer must be shown on
    // "downloading or exporting").
    const [pendingConfirmAction, setPendingConfirmAction] =
        useState<PendingConfirmAction>(null);
    const nameCopy = useCopyToClipboard();
    const idCopy = useCopyToClipboard();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const sensitiveFilter = showSensitiveInfo ? 'none' : 'blur(0.5rem)';
    const sensitiveUserSelect = showSensitiveInfo ? 'auto' : 'none';
    const { exporting, handleDownloadBatch, handleExportBatch } =
        useDicomExport();
    const canExportToTps = AppConfig.isConnectorMode;
    const enableExportMonacoDirFile = useRecoilValue(
        enableExportMonacoDirFileState
    );
    const intl = useIntl();
    const patient = useSelectedPatient();

    // PACS export targets (direction "Out" or "InOut"). Only fetched in
    // connector mode; in standalone mode the download path is the only
    // available action and there's no target to pick.
    const { data: targetsData } = useListTargets(
        {},
        { staleTime: 60 * 1000, enabled: canExportToTps }
    );

    const availableExportTargets = useMemo(() => {
        if (
            !targetsData ||
            typeof targetsData !== 'object' ||
            Array.isArray(targetsData)
        ) {
            return [];
        }
        return Object.entries(targetsData)
            .filter(
                ([, direction]) => direction === 'Out' || direction === 'InOut'
            )
            .map(([name]) => name);
    }, [targetsData]);

    const [selectedExportTargetName, setSelectedExportTargetName] = useState<
        string | null
    >(null);

    // Tracks which action button the user pressed so the spinner shows
    // on the matching button (Download vs Export). `exporting` from
    // useDicomExport flips for both; without this we'd always pin the
    // spinner to the Download button.
    const [runningAction, setRunningAction] = useState<
        'download' | 'export' | null
    >(null);

    // Auto-select the target only when there is exactly one option. When
    // multiple targets are configured we leave the value unset so the user
    // must consciously pick which PACS destination to export to (the export
    // button is disabled via `!selectedExportTargetName` until they do).
    useEffect(() => {
        if (
            availableExportTargets.length === 1 &&
            selectedExportTargetName === null
        ) {
            setSelectedExportTargetName(availableExportTargets[0]);
        }
    }, [availableExportTargets, selectedExportTargetName]);

    const exportableImages = patient ? [...patient.images] : [];
    const exportableDoses = patient ? [...patient.doses] : [];
    const exportableRegistrations: RigidRegistration[] = patient
        ? [...patient.registrations]
        : [];

    // Live structure sets are needed ONLY as the argument to
    // useExportEditing, which is out of scope for the projection
    // migration and still mutates live entities directly. Every other
    // consumer below uses the immutable StructureSetReadModel projections.
    const liveStructureSets = patient ? [...patient.structureSets] : [];

    // Immutable structure-set projections — the read side the modal
    // subtree and the selection/export hooks consume.
    const exportableStructureSets = useExportStructureSetReadModels();

    const {
        selectedImages,
        selectedStructureSetUids,
        selectedDoses,
        selectedRegistrations,
        structureSelections,
        handleSelectImage,
        handleSelectStructureSet,
        handleSelectStructure,
        handleSelectDose,
        handleSelectRegistration,
        handleSelectAll,
        resetSelections,
    } = useExportSelections(
        exportableStructureSets,
        exportableDoses,
        open,
        exportableRegistrations
    );

    const {
        imageEdits,
        ssEdits,
        roiEdits,
        setImageSeriesDescription,
        setImageSeriesNumber,
        setSsLabel,
        setSsApproval,
        setSsReviewer,
        setRoiName,
        setRoiType,
        commitAllEdits,
    } = useExportEditing(exportableImages, liveStructureSets);

    const selectedStructureSets = exportableStructureSets.filter((ss) =>
        selectedStructureSetUids.includes(ss.dicomUid)
    );

    // Exporting or sending an image that is not the whole volume writes a
    // DICOM series holding only the slices that arrived — self-consistent in
    // its own file count, indistinguishable from a complete study downstream,
    // and not the patient. Both actions are refused until the selection is
    // complete. `Image3D.getSlicesAsDicomBlobs` refuses it too; this is the
    // half the user can see and act on.
    const selectionGate = useImagesCompletenessGate(selectedImages);

    const hasSelection =
        selectedImages.length > 0 ||
        selectedStructureSetUids.length > 0 ||
        selectedDoses.length > 0 ||
        selectedRegistrations.length > 0;

    const selectedStructureSetsWithRois: SelectedStructureSetsWithStructures[] =
        selectedStructureSetUids.map((uid) => ({
            structureSetUid: uid,
            selectedRoiIds: structureSelections[uid],
        }));

    const closeConfirmationDialog = () => {
        setConfirmationDialogOpen(false);
        setPendingConfirmAction(null);
    };

    // Applies the staged export edits, surfacing any domain error as a
    // toast instead of letting it bubble out of the click/close handler
    // (which silently aborted the export with no feedback, and left the
    // modal uncloseable from handleClose). Returns false on failure.
    const commitAllEditsSafely = (): boolean => {
        try {
            commitAllEdits();
            return true;
        } catch (e) {
            enqueueSnackbar(
                e instanceof Error
                    ? e.message
                    : 'Failed to apply edits before export.',
                { variant: 'error' }
            );
            return false;
        }
    };

    const handleExport = async () => {
        if (!selectedExportTargetName) {
            return;
        }
        closeConfirmationDialog();
        if (!commitAllEditsSafely()) {
            return;
        }
        setRunningAction('export');
        try {
            await handleExportBatch(
                selectedImages,
                selectedStructureSetsWithRois,
                selectedDoses,
                selectedExportTargetName,
                selectedRegistrations
            );
            resetSelections();
        } finally {
            setRunningAction(null);
        }
    };

    const handleDownload = () => {
        closeConfirmationDialog();
        if (!commitAllEditsSafely()) {
            return;
        }
        setRunningAction('download');
        try {
            handleDownloadBatch(
                selectedImages,
                selectedStructureSetsWithRois,
                selectedDoses,
                enableExportMonacoDirFile,
                selectedRegistrations
            );
        } finally {
            setRunningAction(null);
        }
    };

    const handleConfirm = () => {
        if (pendingConfirmAction === 'export') {
            void handleExport();
        } else if (pendingConfirmAction === 'download') {
            handleDownload();
        }
    };

    const handleDownloadClick = () => {
        // When a dose is part of the selection, the user must first
        // acknowledge the dose scaling / normalization disclaimer
        // regardless of which flow (Download or Export-to-TPS) delivers
        // the data. See SRV-WOR-H-4-1-2.
        if (selectedDoses.length > 0) {
            setPendingConfirmAction('download');
            setConfirmationDialogOpen(true);
            return;
        }
        handleDownload();
    };

    const handleExportClick = () => {
        setPendingConfirmAction('export');
        setConfirmationDialogOpen(true);
    };

    // Newly generated items are the primary reason the user opens the export
    // modal, so auto-expand the first one to draw attention to it.
    const defaultExpanded =
        exportableImages.find((img) => img.isNew)?.dicomUid ??
        exportableStructureSets.find((ss) => ss.isNew)?.dicomUid ??
        exportableDoses.find((dose) => dose.isNew)?.dicomUid ??
        exportableImages[0]?.dicomUid ??
        exportableStructureSets[0]?.dicomUid ??
        exportableDoses[0]?.dicomUid ??
        '';

    const patientDob = patient?.dataSet.PatientBirthDate;
    const patientSexCode = patient?.dataSet.PatientSex;
    const formattedDob = patientDob ? formatDateCompact(patientDob) : undefined;
    const patientAge = patientDob ? calculateAge(patientDob) : undefined;
    const patientSex = patientSexCode
        ? intl.formatMessage({
              id:
                  SEX_MESSAGE_IDS[patientSexCode.toUpperCase()] ??
                  'sex.unknown',
          })
        : undefined;

    const handleClose = () => {
        // Always close, even if applying edits fails — a commit error must
        // never trap the user in the modal.
        commitAllEditsSafely();
        setOpen(false);
        closeConfirmationDialog();
        resetSelections();
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box display="grid" gap={2} minWidth={600}>
                <ExportHeader />
                <ExportConfirmation
                    open={confirmationDialogOpen}
                    setOpen={(isOpen) => {
                        setConfirmationDialogOpen(isOpen);
                        if (!isOpen) {
                            setPendingConfirmAction(null);
                        }
                    }}
                    onConfirm={handleConfirm}
                    selectedDoses={selectedDoses}
                    selectedStructureSets={selectedStructureSets}
                    selectedRegistrations={selectedRegistrations}
                    structureSelections={structureSelections}
                />

                {patient && (
                    <Box
                        display="grid"
                        gridTemplateColumns="auto 1fr"
                        gap="3px 8px"
                        px={0.5}
                        alignItems="center"
                    >
                        <LabelText>
                            <FormattedMessage id="name" />
                        </LabelText>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                                minWidth: 0,
                                '&:hover .copy-icon': { opacity: 1 },
                            }}
                        >
                            <ValueText
                                component="span"
                                title={
                                    showSensitiveInfo
                                        ? patient.dataSet.PatientName
                                        : undefined
                                }
                                sx={{
                                    filter: sensitiveFilter,
                                    userSelect: sensitiveUserSelect,
                                }}
                            >
                                {patient.dataSet.PatientName || '—'}
                            </ValueText>
                            {patient.dataSet.PatientName &&
                                showSensitiveInfo && (
                                    <Tooltip
                                        title={nameCopy.label}
                                        placement="right"
                                    >
                                        <IconButton
                                            size="small"
                                            className="copy-icon"
                                            sx={{
                                                opacity: 0,
                                                transition: 'opacity 0.2s',
                                                flexShrink: 0,
                                            }}
                                            onClick={() =>
                                                nameCopy.copy(
                                                    patient.dataSet.PatientName!
                                                )
                                            }
                                            aria-label={nameCopy.label}
                                        >
                                            <Copy size={10} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                        </Box>

                        <LabelText>
                            <FormattedMessage id="patientId" />
                        </LabelText>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                                minWidth: 0,
                                '&:hover .copy-icon': { opacity: 1 },
                            }}
                        >
                            <ValueText
                                component="span"
                                title={
                                    showSensitiveInfo
                                        ? patient.dataSet.PatientID
                                        : undefined
                                }
                                sx={{
                                    filter: sensitiveFilter,
                                    userSelect: sensitiveUserSelect,
                                }}
                            >
                                {patient.dataSet.PatientID || '—'}
                            </ValueText>
                            {patient.dataSet.PatientID && showSensitiveInfo && (
                                <Tooltip title={idCopy.label} placement="right">
                                    <IconButton
                                        size="small"
                                        className="copy-icon"
                                        sx={{
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            flexShrink: 0,
                                        }}
                                        onClick={() =>
                                            idCopy.copy(
                                                patient.dataSet.PatientID!
                                            )
                                        }
                                        aria-label={idCopy.label}
                                    >
                                        <Copy size={10} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>

                        {formattedDob && (
                            <>
                                <LabelText>
                                    <FormattedMessage id="dob" />
                                </LabelText>
                                <ValueText
                                    component="span"
                                    sx={{
                                        filter: sensitiveFilter,
                                        userSelect: sensitiveUserSelect,
                                    }}
                                >
                                    {patientAge !== undefined
                                        ? `${formattedDob} (${intl.formatMessage(
                                              { id: 'yearsOld' },
                                              { age: patientAge }
                                          )})`
                                        : formattedDob}
                                </ValueText>
                            </>
                        )}

                        {patientSex && (
                            <>
                                <LabelText>
                                    <FormattedMessage id="sex" />
                                </LabelText>
                                <ValueText
                                    component="span"
                                    sx={{
                                        filter: sensitiveFilter,
                                        userSelect: sensitiveUserSelect,
                                    }}
                                >
                                    {patientSex}
                                </ValueText>
                            </>
                        )}
                    </Box>
                )}

                <Divider />

                <Box>
                    <Box maxHeight="50vh" overflow="auto">
                        <ExportableItemsList
                            exportableImages={exportableImages}
                            exportableDoses={exportableDoses}
                            exportableStructureSets={exportableStructureSets}
                            exportableRegistrations={exportableRegistrations}
                            selectedImages={selectedImages}
                            selectedStructureSetUids={selectedStructureSetUids}
                            selectedDoses={selectedDoses}
                            selectedRegistrations={selectedRegistrations}
                            structureSelections={structureSelections}
                            handleSelectImage={handleSelectImage}
                            handleSelectStructureSet={handleSelectStructureSet}
                            handleSelectStructure={handleSelectStructure}
                            handleSelectDose={handleSelectDose}
                            handleSelectRegistration={handleSelectRegistration}
                            handleSelectAll={(selectAll: boolean) =>
                                handleSelectAll(
                                    {
                                        images: exportableImages,
                                        structureSets: exportableStructureSets,
                                        doses: exportableDoses,
                                        registrations: exportableRegistrations,
                                    },
                                    selectAll
                                )
                            }
                            defaultExpanded={defaultExpanded}
                            imageEdits={imageEdits}
                            setImageSeriesDescription={
                                setImageSeriesDescription
                            }
                            setImageSeriesNumber={setImageSeriesNumber}
                            ssEdits={ssEdits}
                            roiEdits={roiEdits}
                            setSsLabel={setSsLabel}
                            setSsApproval={setSsApproval}
                            setSsReviewer={setSsReviewer}
                            setRoiName={setRoiName}
                            setRoiType={setRoiType}
                        />
                    </Box>

                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                        gap={1}
                        mt={2}
                    >
                        {canExportToTps &&
                            availableExportTargets.length > 0 && (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mr="auto"
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        whiteSpace="nowrap"
                                    >
                                        <FormattedMessage id="selectDicomTarget" />
                                        :
                                    </Typography>
                                    <Select
                                        size="small"
                                        value={selectedExportTargetName ?? ''}
                                        onChange={(e) =>
                                            setSelectedExportTargetName(
                                                e.target.value
                                            )
                                        }
                                        disabled={
                                            availableExportTargets.length < 2
                                        }
                                        sx={{ minWidth: 200 }}
                                        data-testid="exportTargetSelect"
                                    >
                                        {availableExportTargets.map(
                                            (target) => (
                                                <MenuItem
                                                    key={target}
                                                    value={target}
                                                >
                                                    {target}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </Box>
                            )}
                        <DisabledHint
                            title={selectionGate.reason}
                            placement="bottom"
                        >
                            <Button
                                data-testid="downloadButton"
                                variant={
                                    canExportToTps ? 'outlined' : 'contained'
                                }
                                color="primary"
                                onClick={handleDownloadClick}
                                disabled={
                                    exporting ||
                                    !hasSelection ||
                                    selectionGate.isBlocked
                                }
                                size={canExportToTps ? 'medium' : 'large'}
                                sx={{
                                    width: canExportToTps ? 120 : 200,
                                }}
                                startIcon={
                                    runningAction === 'download' ? (
                                        <CircularProgress size={12} />
                                    ) : (
                                        <Download size={12} />
                                    )
                                }
                            >
                                <FormattedMessage id="download" />
                            </Button>
                        </DisabledHint>
                        {canExportToTps && (
                            <DisabledHint
                                title={selectionGate.reason}
                                placement="bottom"
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleExportClick}
                                    disabled={
                                        exporting ||
                                        !hasSelection ||
                                        !selectedExportTargetName ||
                                        selectionGate.isBlocked
                                    }
                                    sx={{ width: 120 }}
                                    startIcon={
                                        runningAction === 'export' ? (
                                            <CircularProgress size={12} />
                                        ) : null
                                    }
                                >
                                    <FormattedMessage id="export" />
                                </Button>
                            </DisabledHint>
                        )}
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

export default ExportDatasetModal;
